<template>
	<v-dialog v-model="isOpen" width="600" scrollable persistent>
		<v-card>
			<div v-if="!isAuthorizingStep" class="d-flex justify-end px-1">
				<v-btn
					class="btn-close"
					color="#383838"
					icon
					@click="closeModal">
						<v-icon>mdi-close-circle-outline</v-icon>
				</v-btn>
			</div>

			<!-- Flow selector -->
			<template v-if="!flow">
				<v-card-title>
					{{ $t("ancv.select-flow.title") }}
				</v-card-title>
				<v-card-text class="d-flex flex-column align-center">
					<v-btn
						block
						height="80px"
						class="white--text mt-4 justify-start"
						type="submit"
						color="button"
						x-large
						@click="selectFlow('qrcode')"
					>
					<div class="d-flex">
						<div>
							<v-icon size="40">mdi-qrcode</v-icon>
						</div>
						<div class="text-left ml-2">
							{{ $t("ancv.select-flow.qrcode.title") }} <br />
							<span class="font-weight-regular text-body-1">{{ $t("ancv.select-flow.qrcode.description") }}</span>
						</div>
					</div>
					</v-btn>
					<v-btn
						block
						height="80px"
						class="white--text my-8 justify-start"
						type="submit"
						color="button"
						x-large
						@click="selectFlow('manual')"
					>
						<div class="d-flex">
							<div>
								<v-icon size="40">mdi-dialpad</v-icon>
							</div>
							<div class="text-left ml-2">
								{{ $t("ancv.select-flow.manual.title") }} <br />
								<span class="font-weight-regular text-body-1">{{ $t("ancv.select-flow.manual.description") }}</span>
							</div>
						</div>
					</v-btn>
					<v-btn
						width="420px"
						class="cancelBtn mt-4"
						color="primary"
						x-large
						text
						outlined
						@click="closeModal"
					>
						{{ $t("btn.cancel") }}
					</v-btn>
				</v-card-text>
			</template>

			<!-- QRCode Flow -->
			<template v-if="flow === 'qrcode'">
				<template v-if="isAuthorizingStep">
					<div  class="d-flex justify-center my-4">
						<AncvLoader />
					</div>
					<v-card-title>
						{{ $t("ancv.manual.pending.title") }}
					</v-card-title>
					<v-card-text>
						{{ $t("ancv.manual.pending.description") }}
						<v-alert
							v-if="isPollingTimedOut"
							type="warning"
							text
							class="mt-4"
						>
							{{ $t("ancv.timeout.message") }}

							<v-btn
								block
								class="cancelBtn mt-4"
								color="primary"
								x-large
								text
								outlined
								@click="closeModal"
							>
								{{ $t("btn.cancel") }}
							</v-btn>
						</v-alert>
					</v-card-text>
				</template>
				<template v-else>
					<v-card-title>
						{{ $t("ancv.qrcode.title") }}
					</v-card-title>
					<v-card-text>
						<div v-if="loading || !qrCode" class="d-flex justify-center align-center mb-4">
							<div class="qrcode-loading d-flex justify-center align-center">
								<span>{{ $t("ancv.qrcode.loading") }}...</span>
							</div>
						</div>
						<div v-else class="d-flex justify-center mb-4">
							<img
								:src="`data:image/png;base64,${qrCode}`"
								alt="QR Code"
								class="qr-code-image"
							/>
						</div>

						<v-alert
							v-if="isPollingTimedOut"
							type="warning"
							text
							class="mt-4"
						>
							{{ $t("ancv.timeout.message") }}
						</v-alert>

						<v-btn
							v-if="isPollingTimedOut"
							block
							class="cancelBtn mt-4"
							color="primary"
							x-large
							text
							outlined
							@click="closeModal"
						>
							{{ $t("btn.cancel") }}
						</v-btn>
					</v-card-text>
				</template>
			</template>

			<!-- Manual Flow -->
			<template v-if="flow === 'manual'">
				<template v-if="isAuthorizingStep">
					<div  class="d-flex justify-center my-4">
						<AncvLoader />
					</div>
					<v-card-title>
						{{ $t("ancv.manual.pending.title") }}
					</v-card-title>
					<v-card-text>
						{{ $t("ancv.manual.pending.description") }}
						<v-alert
							v-if="isPollingTimedOut"
							type="warning"
							text
							class="mt-4"
						>
							{{ $t("ancv.timeout.message") }}
						</v-alert>

						<v-btn
							v-if="isPollingTimedOut"
							block
							class="cancelBtn mt-4"
							color="primary"
							x-large
							text
							outlined
							@click="closeModal"
						>
							{{ $t("btn.cancel") }}
						</v-btn>
					</v-card-text>
				</template>
				<template v-else>
					<v-card-title>
						{{ $t("ancv.manual.title") }}
					</v-card-title>
					<v-card-text>
						<div class="mt-4">
							{{ $t("ancv.manual.description") }}
						</div>
						<div>
							<v-text-field
								v-model="beneficiaryId"
								outlined
							>
							</v-text-field>
						</div>
						<v-btn
							:disabled="!beneficiaryId || isManualFlowConfirmed"
							block
							class="white--text"
							type="submit"
							color="button"
							x-large
							@click="confirmManualFlow"
						>
							{{ $t("btn.confirm") }}
						</v-btn>
						<v-btn
							:disabled="isManualFlowConfirmed"
							block
							class="cancelBtn mt-4"
							color="primary"
							x-large
							text
							outlined
							@click="closeModal"
						>
							{{ $t("btn.cancel") }}
						</v-btn>
					</v-card-text>
				</template>
			</template>
		</v-card>
	</v-dialog>
</template>

<script>
import AncvLoader from "@/components/AncvLoader.vue";

export default {
	name: "PaymentMethodANCVModal",
	components: { AncvLoader },
	props: {
		isModalOpen: {
			type: Boolean,
			default: false,
		},
		qrCode: {
			type: String,
			default: null,
		},
		isManualFlow: {
			type: Boolean,
			default: false,
		},
		isAuthorizingStep: {
			type: Boolean,
			default: false,
		},
		isPollingTimedOut: {
			type: Boolean,
			default: false,
		},
		loading: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			isOpen: false,
			flow: '',
			beneficiaryId: null,
			isManualFlowConfirmed: false,
		};
	},
	methods:{
		closeModal() {
			this.$emit("close");
			this.isOpen = false;
			this.isManualFlowConfirmed = false;
			// Wait for modal to close before resetting values
			setTimeout(() => {
				this.flow = '';
			}, 500);
		},
		selectFlow(flow) {
			this.flow = flow;
			if(flow === 'qrcode') {
				this.$emit("start-ancv-process");
			}
		},
		confirmManualFlow() {
			this.isManualFlowConfirmed = true;
			this.$emit("start-ancv-process", this.beneficiaryId);
		}
	},
	watch: {
		isModalOpen(newVal) {
			this.isOpen = newVal;
			if(!newVal) {
				// Reset values when modal is closed
				this.isManualFlowConfirmed = false;
			}
		},
		isManualFlow: {
			immediate: true,
			handler(newVal) {
				if(newVal) {
					this.flow = 'manual';
				}
			}
		}
	},
}
</script>

<style lang="scss" scoped>

.qr-code-image,
.qrcode-loading {
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	padding: 16px;
	background-color: white;
}

.qrcode-loading {
	width: 334px;
	height: 334px;
	font-size: 24px;
}

.qr-code-image {
	max-width: 100%;
	height: auto;
}

.cancelBtn {
	border-style: solid;
	border-width: 1px;
	border-color: #e37222 !important;
}
</style>
